import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useAuthContext } from "./authContext";
import {
  getActividad,
  nuevaActividad,
  saveActividad,
  marcarLeidaActividad,
} from "../application/actividad";

export const ActividadContext = createContext();

// Espejo de `actividad` para leer el valor previo al revertir sin depender
// de closures viejas (mismo truco que NotasContext).
const ActividadContextProvider = ({ children }) => {
  const { user } = useAuthContext();
  const [actividad, setActividad] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const actividadRef = useRef([]);
  useEffect(() => {
    actividadRef.current = actividad;
  }, [actividad]);

  const refreshActividad = useCallback(async () => {
    setLoading(true);
    try {
      setActividad(await getActividad());
    } catch (error) {
      console.error("Error al cargar actividad:", error);
    } finally {
      setLoading(false);
      setInitialLoaded(true);
    }
  }, []);

  useEffect(() => {
    refreshActividad();
  }, [refreshActividad]);

  // Registra un evento de actividad. Es "fire and forget": nunca debe hacer
  // fallar la accion real (cobro, aumento, alta, etc) que la dispara, asi que
  // se llama sin await desde los puntos de insercion y acá solo logueamos.
  const registrarActividad = useCallback(
    async (tipo, datos) => {
      const item = nuevaActividad({
        tipo,
        autorUid: user?.uid || "",
        autorNombre: user?.displayName || user?.email || "",
        ...datos,
      });
      setActividad((prev) => [item, ...prev]);
      try {
        await saveActividad(item);
      } catch (error) {
        console.error("Error al registrar actividad:", error);
        setActividad((prev) =>
          prev.filter((a) => a.idFirebase !== item.idFirebase)
        );
      }
    },
    [user]
  );

  const marcarLeida = useCallback(
    async (id) => {
      const uid = user?.uid;
      if (!uid) return;
      const previo = actividadRef.current.find((a) => a.idFirebase === id);
      if (!previo || previo.leidoPor?.includes(uid)) return;
      const leidoPor = [...(previo.leidoPor || []), uid];
      setActividad((prev) =>
        prev.map((a) => (a.idFirebase === id ? { ...a, leidoPor } : a))
      );
      try {
        await marcarLeidaActividad(id, leidoPor);
      } catch (error) {
        console.error("Error al marcar actividad como leida:", error);
        setActividad((prev) =>
          prev.map((a) => (a.idFirebase === id ? previo : a))
        );
      }
    },
    [user]
  );

  const marcarTodoLeido = useCallback(async () => {
    const uid = user?.uid;
    if (!uid) return;
    const pendientes = actividadRef.current.filter(
      (a) => !a.leidoPor?.includes(uid)
    );
    if (!pendientes.length) return;
    setActividad((prev) =>
      prev.map((a) =>
        a.leidoPor?.includes(uid) ? a : { ...a, leidoPor: [...(a.leidoPor || []), uid] }
      )
    );
    await Promise.all(
      pendientes.map((a) =>
        marcarLeidaActividad(a.idFirebase, [...(a.leidoPor || []), uid]).catch(
          (error) => console.error("Error al marcar actividad como leida:", error)
        )
      )
    );
  }, [user]);

  const actividadNoLeida = useMemo(() => {
    const uid = user?.uid;
    if (!uid) return [];
    return actividad.filter((a) => !a.leidoPor?.includes(uid));
  }, [actividad, user]);

  const value = useMemo(
    () => ({
      actividad,
      loading,
      initialLoaded,
      refreshActividad,
      registrarActividad,
      marcarLeida,
      marcarTodoLeido,
      actividadNoLeida,
    }),
    [
      actividad,
      loading,
      initialLoaded,
      refreshActividad,
      registrarActividad,
      marcarLeida,
      marcarTodoLeido,
      actividadNoLeida,
    ]
  );

  return (
    <ActividadContext.Provider value={value}>
      {children}
    </ActividadContext.Provider>
  );
};

export default ActividadContextProvider;
